import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useCheckLimitStore, useAccountsStore, useDictionariesStore, useInternalStore, useNotificationStore } from '@lib/store';
import { AmountUtils } from '@shared/utils';
import { RequestType } from '@shared/api';
import { Button, Constants, ErrorMessage, Input, ItemsType, Modal, Select, Spinner } from '@shared/widgets';
import { ApprovePasswordForm } from './approvePasswordForm';

enum Step {
  Payment = 'payment',
  Password = 'password',
}

interface IForm {
  debitAccount?: ItemsType;
  creditAccount?: ItemsType;
  amount?: number;
  currency?: ItemsType;
}
const DEFAULT_VALUES: IForm = { debitAccount: undefined, creditAccount: undefined, amount: undefined, currency: undefined };

interface TransferBetweenMyAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TransferBetweenMyAccountModal = (props: TransferBetweenMyAccountModalProps) => {
  const accountStore = useAccountsStore();
  const dictionariesStore = useDictionariesStore();
  const checkLimitStore = useCheckLimitStore();
  const internalStore = useInternalStore();
  const notificationStore = useNotificationStore();

  const [step, setStep] = useState<Step>(Step.Payment);

  const { formState, control, handleSubmit, watch, setValue, reset } = useForm<IForm>({ defaultValues: DEFAULT_VALUES });
  const debitAccount = watch('debitAccount');
  const creditAccount = watch('creditAccount');
  const amount = watch('amount');
  const currency = watch('currency');

  useEffect(() => {
    setValue('debitAccount', accountOptions[0]);
    setValue('creditAccount', accountOptions[1]);
  }, [props.isOpen]);

  const cancelHandler = () => {
    props.onClose();
    reset(DEFAULT_VALUES);
  };
  const submitHandler = async (data: IForm) => {
    if (Number(debitAccount?.label) < data.amount!) {
      notificationStore.setError(Constants.AMOUNT_ERROR);
      return;
    }

    const result = await checkLimitStore.checkLimit({
      requestType: RequestType.Refill,
      accountIdFrom: data.debitAccount?.value,
      accountIdTo: data.creditAccount?.value,
      amount: Number(data.amount),
      currency: data.currency?.value,
    });
    if (result.data?.LimitExceeded) {
      notificationStore.setError(result.data.Note);
      return;
    }

    const result2 = await internalStore.createInternal({
      SourceAccount: data.debitAccount?.value,
      TargetAccount: data.creditAccount?.value,
      Amount: data.amount,
      Currency: data.currency?.value,
    });
    if (result2) setStep(Step.Password);
  };
  const submitApprovePasswordHandler = async () => {
    props.onClose();
    setStep(Step.Payment);
    reset(DEFAULT_VALUES);
  };

  const accountOptions = useMemo(
    () => accountStore.data.map((item) => ({ value: item.Id, label: item.Amount, leftLabel: item.Currency })),
    [accountStore.data],
  );
  const currencyOptions = useMemo(() => {
    const result =
      dictionariesStore.data?.Currency.filter((item) => item.Name === debitAccount?.leftLabel || item.Name === creditAccount?.leftLabel).map(
        (item) => ({
          value: item.Id,
          label: item.Name,
          leftLabel: item.Name,
        }),
      ) || [];
    if (result.length) setValue('currency', result[0]);
    return result;
  }, [dictionariesStore.data, debitAccount, creditAccount]);
  return (
    <Modal isOpen={props.isOpen} title='Internal Transfer'>
      <Spinner show={formState.isSubmitting} fullscreen />
      {step === Step.Payment && (
        <form className='w-full' onSubmit={handleSubmit(submitHandler)}>
          <div className='flex flex-col gap-3'>
            <Controller
              name='debitAccount'
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Debit Account</div>
                  <Select
                    double
                    items={accountOptions}
                    value={value?.value}
                    onChange={(e) => {
                      if (e?.value === creditAccount?.value) setValue('creditAccount', debitAccount);
                      onChange(e);
                    }}
                  />
                </div>
              )}
            />
            <Controller
              name='creditAccount'
              control={control}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Credit Account</div>
                  <Select
                    double
                    items={accountOptions.length > 1 ? accountOptions : []}
                    value={value?.value}
                    onChange={(e) => {
                      if (e?.value === debitAccount?.value) setValue('debitAccount', creditAccount);
                      onChange(e);
                    }}
                  />
                </div>
              )}
            />
            <Controller
              name='amount'
              control={control}
              rules={{ required: 'This field is required', maxLength: 100 }}
              render={({ field: { value, onChange }, fieldState: { error } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Amount</div>
                  <Input
                    className='!h-[46px]'
                    variant='outlined'
                    rounded
                    type='number'
                    error={error?.message}
                    name='Amount'
                    placeholder='Enter Amount'
                    required
                    value={value}
                    onChange={(value) => AmountUtils.amountChange(value, onChange)}
                  />
                  <ErrorMessage text={error?.message} />
                </div>
              )}
            />
            <Controller
              name='currency'
              control={control}
              defaultValue={currencyOptions?.[0]}
              rules={{ required: 'This field is required' }}
              render={({ field: { value, onChange } }) => (
                <div className='flex w-full flex-col gap-1'>
                  <div className='text-xs font-semibold text-[#929292] uppercase'>Currency</div>
                  <Select value={value?.value} onChange={onChange} items={currencyOptions} placeholder='CURRENCY' />
                </div>
              )}
            />
          </div>
          <div className='mt-6.5 flex items-center justify-between gap-2.5'>
            <Button className='!h-[46px]' color='outlined' fullWidth onClick={cancelHandler}>
              Cancel
            </Button>
            <Button className='!h-[46px]' fullWidth type='submit' disabled={!formState.isValid || formState.isSubmitting}>
              Continue
            </Button>
          </div>
        </form>
      )}
      {step === Step.Password && (
        <ApprovePasswordForm
          data={[
            { title: 'Debit Account:', label: debitAccount?.leftLabel, value: AmountUtils.transformToAmount(debitAccount?.label) },
            { title: 'Credit Account:', label: creditAccount?.leftLabel, value: AmountUtils.transformToAmount(creditAccount?.label) },
            { title: 'Amount:', label: currency?.leftLabel, value: AmountUtils.transformToAmount(amount) },
          ]}
          requestId={internalStore.data?.Id}
          onBack={() => setStep(Step.Payment)}
          onSubmit={submitApprovePasswordHandler}
        />
      )}
    </Modal>
  );
};
